import type { ReactNode } from 'react';
import {
  Code,
  FormLayoutGroup,
  Icon,
  InlineStack,
  Link,
  Paragraph,
  Select,
  TextArea,
  TextField,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import type { BucketConfigDraft, BucketConfigField } from '@/features/warehouse-sync';
import type { ProviderFormProps } from './types';

/** Stand-ins in the placeholder and in the upload-path preview until the user types. */
export const DEFAULT_BUCKET_NAME = 'my-bucket';
export const DEFAULT_BUCKET_PATH = 'my-path';

/** `bucket_name` → `bucket-name`, so the selectors read the same as the wire names. */
function fieldTestId(field: BucketConfigField): string {
  return field.replaceAll('_', '-');
}

/** Blank means "cleared", not "invalid" — Pluma renders any string as an error. */
function errorFor(form: ProviderFormProps, field: BucketConfigField): string | undefined {
  const message = form.errors[field];
  return isEmptyString(message) ? undefined : message;
}

/**
 * The value, error and handler wiring shared by every field kind. Typed against
 * the value alone, so it fits an `input` and a `textarea` alike.
 */
function fieldBindings(form: ProviderFormProps, field: BucketConfigField) {
  return {
    name: fieldTestId(field),
    value: form.config[field] ?? '',
    error: errorFor(form, field),
    isDisabled: form.isDisabled,
    onInput: (event: Event & { target: { value: string } }) => form.onChange(field, event.target.value),
    onBlur: () => form.onBlur(field),
  };
}

type ConfigTextFieldProps = {
  form: ProviderFormProps;
  field: BucketConfigField;
  label: string;
  placeholder?: string;
  /** Renders masked, with Pluma's reveal toggle. */
  isSecret: boolean;
  autofocus: boolean;
};

/** One credential input, wired to the draft config by its wire field name. */
export function ConfigTextField({ form, field, label, placeholder, isSecret, autofocus }: ConfigTextFieldProps) {
  return (
    <TextField
      data-test-warehouse-input={fieldTestId(field)}
      label={label}
      placeholder={placeholder}
      type={isSecret ? 'password' : 'text'}
      autofocus={autofocus}
      {...fieldBindings(form, field)}
    />
  );
}

type ConfigTextAreaProps = {
  form: ProviderFormProps;
  field: BucketConfigField;
  label: string;
  rows: number;
};

/** A credential too long for one line — a service account key, a SAS URL. */
export function ConfigTextArea({ form, field, label, rows }: ConfigTextAreaProps) {
  return (
    <TextArea
      data-test-warehouse-textarea={fieldTestId(field)}
      label={label}
      rows={rows}
      {...fieldBindings(form, field)}
    />
  );
}

type RegionSelectProps = {
  form: ProviderFormProps;
  label: string;
  options: Array<{ value: string; label: string }>;
};

/** The region list is long enough that AWS needs the filter; Yandex keeps parity. */
export function RegionSelect({ form, label, options }: RegionSelectProps) {
  return (
    <Select
      data-test-warehouse-select="region"
      label={label}
      options={options}
      placeholder="Choose a region…"
      isSearchable
      value={form.config.region ?? ''}
      error={errorFor(form, 'region')}
      isDisabled={form.isDisabled}
      // A select has no partial state to blur out of, so the choice is validated
      // as soon as it is made.
      onChange={(value) => {
        form.onChange('region', value ?? '');
        form.onBlur('region');
      }}
    />
  );
}

type BucketNameAndPathFieldsProps = {
  form: ProviderFormProps;
};

/** The name/path pair every provider but Azure opens with. */
export function BucketNameAndPathFields({ form }: BucketNameAndPathFieldsProps) {
  return (
    <FormLayoutGroup>
      <ConfigTextField
        form={form}
        field="bucket_name"
        label="Bucket name"
        placeholder={DEFAULT_BUCKET_NAME}
        isSecret={false}
        autofocus
      />
      <ConfigTextField
        form={form}
        field="bucket_path"
        label="Bucket path"
        placeholder={DEFAULT_BUCKET_PATH}
        isSecret={false}
        autofocus={false}
      />
    </FormLayoutGroup>
  );
}

/**
 * `bucket/path` for the preview line, or the placeholder pair while both are empty
 * — so the shape of the destination is visible before anything is typed.
 */
export function bucketPreviewPath(config: BucketConfigDraft): string {
  if (isEmptyString(config.bucket_name) && isEmptyString(config.bucket_path)) {
    return `${DEFAULT_BUCKET_NAME}/${DEFAULT_BUCKET_PATH}`;
  }
  return `${config.bucket_name ?? ''}/${config.bucket_path ?? ''}`;
}

type UploadPathPreviewProps = {
  /** The scheme shown ahead of the path, e.g. `s3`. */
  scheme: string;
  path: string;
  /** The console link behind the help icon. Azure publishes no such console. */
  tooltip?: ReactNode;
};

/** Where the exported files will land, echoed back as the user types. */
export function UploadPathPreview({ scheme, path, tooltip }: UploadPathPreviewProps) {
  const target = <Code>{`${scheme}://${path}`}</Code>;

  return (
    <Paragraph data-test-warehouse-upload-path variant="secondary" sizeVariant="xs">
      Files will be uploaded to{' '}
      {tooltip == null ? (
        target
      ) : (
        <Tooltip>
          <InlineStack as="span" alignBlock="center" gap="050">
            {target}
            <Icon name="help" size="sm" />
          </InlineStack>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      )}
    </Paragraph>
  );
}

type CredentialsHelpProps = {
  /** Where the provider documents its keys. */
  children: ReactNode;
};

/** The "Where can I find my keys?" hint that sits above a credential field. */
export function CredentialsHelp({ children }: CredentialsHelpProps) {
  return (
    <Paragraph variant="secondary" sizeVariant="xs" textAlign="right">
      <Tooltip>
        <InlineStack as="span" alignBlock="center" gap="050">
          <span>Where can I find my keys?</span>
          <Icon name="help" size="sm" />
        </InlineStack>
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    </Paragraph>
  );
}

type ConsoleLinkProps = {
  href: string;
  children: ReactNode;
};

/** An outbound link inside tooltip contents, which supply their own text colour. */
export function TooltipLink({ href, children }: ConsoleLinkProps) {
  return (
    <Link variant="secondary" isExternal={true} href={href} color="inherit">
      {children}
    </Link>
  );
}
